import { getAllCookies } from "./cookies";

/**
 * An ad network whose identifiers the tag collects.
 *
 * - `params`: landing-URL parameters set on ad clicks (click IDs).
 * - `cookies` / `cookiePrefixes`: first-party cookies the network's own pixel
 *   sets, used by conversion APIs to match the browser.
 */
interface AdNetwork {
  network: string;
  params: readonly string[];
  cookies?: readonly string[];
  cookiePrefixes?: readonly string[];
}

/**
 * Every ad network the tag collects identifiers for. To support a new
 * network, add it here and nowhere else.
 *
 * Values are sent keyed by the original parameter / cookie name; the backend
 * owns their interpretation (e.g. building Meta's `fbc`, parsing `_gcl_aw`).
 */
export const AD_NETWORKS: readonly AdNetwork[] = [
  {
    network: "google",
    params: [
      "gclid",
      "gbraid",
      "wbraid",
      "dclid",
      "gad_source",
      "gad_campaignid",
    ],
    cookiePrefixes: ["_gcl_"],
  },
  { network: "meta", params: ["fbclid"], cookies: ["_fbp", "_fbc"] },
  { network: "linkedin", params: ["li_fat_id"], cookies: ["li_fat_id"] },
  {
    network: "microsoft",
    params: ["msclkid"],
    cookies: ["_uetvid", "_uetmsclkid"],
  },
  {
    network: "reddit",
    params: ["rdt_cid"],
    cookies: ["_rdt_uuid", "_rdt_cid"],
  },
  { network: "tiktok", params: ["ttclid"], cookies: ["_ttp"] },
  { network: "x", params: ["twclid"], cookies: ["_twclid"] },
  { network: "pinterest", params: ["epik"], cookies: ["_epik"] },
  { network: "snap", params: ["ScCid"], cookies: ["_scid"] },
  { network: "quora", params: ["qclid"] },
  { network: "klaviyo", params: ["ko_click_id"] },
];

/** Lower-cased URL parameter name -> canonical name. */
const CLICK_ID_PARAMS = new Map<string, string>(
  AD_NETWORKS.flatMap(({ params }) =>
    params.map((p) => [p.toLowerCase(), p] as const)
  )
);

const AD_COOKIES = new Set<string>(
  AD_NETWORKS.flatMap(({ cookies }) => cookies ?? [])
);

const AD_COOKIE_PREFIXES: readonly string[] = AD_NETWORKS.flatMap(
  ({ cookiePrefixes }) => cookiePrefixes ?? []
);

/** Guards the payload against oversized or malformed cookie values. */
const MAX_AD_ID_LENGTH = 256;

/**
 * Returns the ad click IDs present in the given URL parameters, keyed by
 * their canonical parameter name. Parameter names are matched
 * case-insensitively, since some redirectors change their case.
 */
export function getClickIdsFromParams(
  params: URLSearchParams
): Record<string, string> | undefined {
  const clickIds: Record<string, string> = {};
  params?.forEach((value, key) => {
    const canonical = CLICK_ID_PARAMS.get(key.toLowerCase());
    if (canonical != null && value !== "" && clickIds[canonical] == null) {
      clickIds[canonical] = value.slice(0, MAX_AD_ID_LENGTH);
    }
  });
  if (Object.keys(clickIds).length === 0) return undefined;
  return clickIds;
}

function isAdCookie(name: string): boolean {
  return (
    AD_COOKIES.has(name) ||
    AD_COOKIE_PREFIXES.some((prefix) => name.startsWith(prefix))
  );
}

/**
 * Returns the browser IDs that ad pixels have stored in first-party cookies,
 * keyed by cookie name. Read on every event rather than cached, because
 * pixels usually load after this tag and update their cookies on new clicks.
 */
export function getAdCookies(): Record<string, string> | undefined {
  const adCookies: Record<string, string> = {};
  try {
    for (const [name, value] of Object.entries(getAllCookies())) {
      if (value !== "" && isAdCookie(name)) {
        adCookies[name] = value.slice(0, MAX_AD_ID_LENGTH);
      }
    }
  } catch {
    // cookies can be unavailable (sandboxed iframe)
    return undefined;
  }
  if (Object.keys(adCookies).length === 0) return undefined;
  return adCookies;
}
